import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  DialogTitle,
  Typography,
} from '@mui/material';
// adding App Insights hook import for telemetry logging
import { useAppInsights } from 'src/hooks/useAppInsights';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import { useEffect, useState, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from 'src/hooks';
import Storage from 'src/utils/storage/storage';
import { AlertDialog, DialogResult } from 'src/components/analytics';
import { transformDynamicFormValueToFilterPayload } from 'src/components/filters/utils/filter-form-utils';
import { MultiSelect } from 'react-multi-select-component';
import FilterLoadingSkeleton from 'src/components/skeletons/filtersLoadingSkeleton';
import {
  getTopLineInputsData,
  resetTopLineInputs,
  updateTableRowsForBrand,
} from '../../store/pricing-simulator-slice';
import TableSkeletonContainer from '../simulation-table-container/table-skeleton';
import TopLineInputsTable from './topline-input-table';
import { getRowsForUpdatedValue } from './topline-input.util';
import { IMPLEMENTATION_PERIOD_1_KEY, IMPLEMENTATION_PERIOD_2_KEY } from 'src/constants';
import { updateDropdownSelection } from '../../../common/features/table/store/table-slice';



interface ITopLineInputsDialogProps {
  /**
   * Boolean indicating the state of dialog visibility
   */
  isOpen: boolean;
  onClose: (isSummaryRefreshRequire: boolean) => void;
}

export default function TopLineInputsDialog({
  isOpen,
  onClose,
}: ITopLineInputsDialogProps) {
  const appDispatch = useAppDispatch();

  // adding App Insights hook initialization for telemetry logging
  const { trackEvent } = useAppInsights();

  const { moduleNameToModuleIdMap } = useAppSelector(
    state => state.simulatorHome
  );
  const { source: sourceId } = Storage.getSourceId();
  const { id: scenarioId } = useParams();

  const {
    globalFilterValue,
    globalFilterCombinations,
    globalFilterFormFields,
  } = useAppSelector(state => state.filters);

  const {
    fetchingTopLineTableData,
    topLineInputTableData,
    enableTopLineApplyButton,
    topLineFieldsPriorityData,
  } = useAppSelector(state => state.pricingSimulator);

  let fieldsData = JSON.parse(JSON.stringify(topLineFieldsPriorityData));
  fieldsData = fieldsData.reduce((acc, item) => {
    acc.push({ label: item.alias, value: item.name, order: item.order });
    return acc;
  }, []);

  const [isConfirmationDialogOpen, setConfirmationDialogOpen] = useState(false);

  // local state for loading fields dropdown
  const [isFieldsLoaded, setFieldsLoaded] = useState(false);

  const latestPeriods = useRef({
    [IMPLEMENTATION_PERIOD_1_KEY]: null as number | string | null,
    [IMPLEMENTATION_PERIOD_2_KEY]: null as number | string | null,
  });

  // local state for active fields
  const [currActiveField, setCurrActiveField] = useState({
    field1: [fieldsData[0]],
    field2: [fieldsData[1]],
  });

  // Stores the last applied field values that should be shown in table
  const [appliedFields, setAppliedFields] = useState({
    field1: fieldsData.length ? [fieldsData[0]] : [],
    field2: [],
  })

  // function for disabling 2nd field options as per the 1st field selected
  const disableFieldOptions = val => {
    const currFieldData = JSON.parse(JSON.stringify(fieldsData));
    currFieldData.forEach(ele => {
      if (ele.order <= val[0].order) {
        // eslint-disable-next-line no-param-reassign
        ele.disabled = true;
      }
    });

    return currFieldData;
  };

  // default state for fields dropdown options
  const [fieldsDropdownData, setFieldsDropdownData] = useState({
    field1: fieldsData,
    field2: disableFieldOptions(fieldsData.filter(ele => ele.order === 1)),
  });

  // function for updating field dropdown value
  const onFieldChange = (key, val) => {
    // adding telemetry log for field selection change
    trackEvent('TopLineInputs_FieldChanged', {
      fieldKey: key,
      selectedField: val[0]?.value,
      selectedLabel: val[0]?.label,
      scenarioId: String(scenarioId || 'new'),
      moduleId: String(moduleNameToModuleIdMap.pricing || ''),
      sourceId: String(sourceId || '')
    });

    if (key === 'field1') {
      // filtering 2nd field options based on the 1st field selection
      let filteredField2Data = JSON.parse(JSON.stringify(fieldsData));

      filteredField2Data = disableFieldOptions(val);

      // reseting 2nd field to empty
      setCurrActiveField(prevState => ({
        ...prevState,
        field2: filteredField2Data.filter(e => !e.disabled).length
          ? [filteredField2Data.filter(e => !e.disabled)[0]]
          : [],
      }));

      if (filteredField2Data.length) {
        // updating 2nd field dropdown data
        setFieldsDropdownData(prevState => ({
          ...prevState,
          field2: filteredField2Data,
        }));
      }
    }

    setCurrActiveField(prevState => ({
      ...prevState,
      [key]: [val[0]],
    }));
  };

  // function for getting selected fields
  const getSelectedFields = () =>
    Object.keys(currActiveField)
      .filter(ele => currActiveField[ele].length)
      .map(ele => currActiveField[ele][0]?.value);

  // function for calling get topline inputs data api
  const callTopLineInputsApi = () => {
    // adding telemetry log for topline API call
    trackEvent('TopLineInputs_DataRequested', {
      selectedFields: getSelectedFields().join(','),
      scenarioId: String(scenarioId || 'new'),
      moduleId: String(moduleNameToModuleIdMap.pricing || ''),
      sourceId: String(sourceId || '')
    });

    const filters = transformDynamicFormValueToFilterPayload({
      combinations: globalFilterCombinations,
      formFields: globalFilterFormFields,
      formValue: globalFilterValue,
    });
    appDispatch(
      getTopLineInputsData({
        sourceId,
        moduleId: moduleNameToModuleIdMap.pricing,
        scenarioId: +scenarioId,
        filterInput: { filter_inputs: filters },
        toplineFields: getSelectedFields(),
      })
    ).then(() => {
      setFieldsLoaded(true);
      // Update applied fields after API call
      setAppliedFields(
        {
          field1: currActiveField.field1,
          field2: currActiveField.field2,
        }
      );
    }
    );
  };

  // adding telemetry log for dialog open
  useEffect(() => {
    trackEvent('TopLineInputs_DialogOpened', {
      scenarioId: String(scenarioId || 'new'),
      moduleId: String(moduleNameToModuleIdMap.pricing || ''),
      sourceId: String(sourceId || '')
    });

    callTopLineInputsApi();
  }, []);

  const [dialogTitle, setDialogTitle] = useState('');
  const [dialogMessage, setDialogMessage] = useState('');
  const [currentActiveDialog, setActiveDialog] = useState('');
  const dialogMessageObj = {
    submitDialog:
      'Note! This will reset all the inputs in the topline table, Are you sure you want to proceed?',
    resetDialog:
      'Resetting will clear all the values and restore all topline input values to default. This action cannot be undone. Are you sure you want to proceed?',
    applyDialog: `Note! The changes you are about to Submit will be applied to all PPGs of the selected level, and any changes made at lower levels will be overwritten. 
       . Price Action 1 will override Price Action 1 and will reset Price Action 2 at lower levels
       . Price Action 2 will override Price Action 2
       Are you sure you want to proceed?`,
  };

  const dialogTitleObj = {
    submitDialog: 'Submit',
    resetDialog: 'Reset',
    applyDialog: 'Apply',
  };

  /**
   * Creates a payload out of the table rows with selected opportunities
   * of each PPG/SKU and calls an API to save
   */
  const handleToplineInputSubmit = (): void => {
    // adding telemetry log for topline input submission
    trackEvent('TopLineInputs_Submitted', {
      scenarioId: String(scenarioId || 'new'),
      moduleId: String(moduleNameToModuleIdMap.pricing || ''),
      sourceId: String(sourceId || ''),
      rowCount: topLineInputTableData?.row?.length || 0,
      appliedFields: Object.values(appliedFields).flat().map(f => f.value).join(',')
    });

    onClose(true);
    const updatedRows = getRowsForUpdatedValue(
      topLineInputTableData.row,
      topLineInputTableData.header
    );
    if (updatedRows) {
      appDispatch(updateTableRowsForBrand({ updatedRows }));

      //On apply update the global PA value
      const { pricing_implement_after_cal_1, pricing_implement_after_cal_2 } = latestPeriods.current;
      if (pricing_implement_after_cal_1 !== null) {
        appDispatch(updateDropdownSelection({
          columnKey: 'pricing_implement_after_cal_1',
          value: pricing_implement_after_cal_1,
        }));
      }
      if (pricing_implement_after_cal_2 !== null) {
        appDispatch(updateDropdownSelection({
          columnKey: 'pricing_implement_after_cal_2',
          value: pricing_implement_after_cal_2,
        }));
      }
    };
  }

  // confirmation dialog function for apply, reset & submit
  const handleConfirmationDialogClose = (result: DialogResult) => {
    if (result === DialogResult.YES && currentActiveDialog === 'resetDialog') {
      // adding telemetry log for reset action
      trackEvent('TopLineInputs_ResetConfirmed', {
        scenarioId: String(scenarioId || 'new'),
        moduleId: String(moduleNameToModuleIdMap.pricing || ''),
        sourceId: String(sourceId || '')
      });
      appDispatch(resetTopLineInputs());
    }
    if (result === DialogResult.YES && currentActiveDialog === 'submitDialog') {
      // adding telemetry log for submit action
      trackEvent('TopLineInputs_SubmitConfirmed', {
        scenarioId: String(scenarioId || 'new'),
        moduleId: String(moduleNameToModuleIdMap.pricing || ''),
        sourceId: String(sourceId || '')
      });
      callTopLineInputsApi();
    }
    if (result === DialogResult.YES && currentActiveDialog === 'applyDialog') {
      // adding telemetry log for apply action
      trackEvent('TopLineInputs_ApplyConfirmed', {
        scenarioId: String(scenarioId || 'new'),
        moduleId: String(moduleNameToModuleIdMap.pricing || ''),
        sourceId: String(sourceId || '')
      });
      handleToplineInputSubmit();
    }

    setConfirmationDialogOpen(false);
  };

  const handleResetAction = (dialogType): void => {
    // adding telemetry log for reset button click
    trackEvent('TopLineInputs_ResetClicked', {
      scenarioId: String(scenarioId || 'new'),
      moduleId: String(moduleNameToModuleIdMap.pricing || ''),
      sourceId: String(sourceId || '')
    });
    setActiveDialog(dialogType);
    setDialogTitle(dialogTitleObj[dialogType]);
    setDialogMessage(dialogMessageObj[dialogType]);
    setConfirmationDialogOpen(true);
  };

  const handleSubmitAction = dialogType => {
    // adding telemetry log for submit button click
    trackEvent('TopLineInputs_SubmitClicked', {
      scenarioId: String(scenarioId || 'new'),
      moduleId: String(moduleNameToModuleIdMap.pricing || ''),
      sourceId: String(sourceId || '')
    });
    setActiveDialog(dialogType);
    setDialogTitle(dialogTitleObj[dialogType]);
    setDialogMessage(dialogMessageObj[dialogType]);
    setConfirmationDialogOpen(true);
  };

  const handleApplyAction = dialogType => {
    // adding telemetry log for apply button click
    trackEvent('TopLineInputs_ApplyClicked', {
      scenarioId: String(scenarioId || 'new'),
      moduleId: String(moduleNameToModuleIdMap.pricing || ''),
      sourceId: String(sourceId || ''),
      isButtonEnabled: enableTopLineApplyButton
    });
    setActiveDialog(dialogType);
    setDialogTitle(dialogTitleObj[dialogType]);
    setDialogMessage(dialogMessageObj[dialogType]);
    setConfirmationDialogOpen(true);
  };

  // function for enabling single select in dropdown
  const customSingleRender = ({ checked, option, onClick, disabled }) => (
    <div className={`item-renderer ${disabled && 'disabled'}`}>
      <input
        type='radio'
        onChange={onClick}
        checked={checked}
        tabIndex={-1}
        disabled={disabled}
      />
      <span>{option.label}</span>
    </div>
  );

  return (
    <>
      <Dialog
        sx={{ '.MuiDialog-paper': { width: '80%' } }}
        maxWidth='xl'
        open={isOpen}
        onClose={(event, reason) => {
          if (reason && reason === 'backdropClick') return;
          // adding telemetry log for dialog close
          trackEvent('TopLineInputs_DialogClosed', {
            scenarioId: String(scenarioId || 'new'),
            moduleId: String(moduleNameToModuleIdMap.pricing || ''),
            sourceId: String(sourceId || ''),
            reason: reason || 'user-action'
          });
          onClose(true);
        }}
        disableEscapeKeyDown
      >
        <DialogTitle
          style={{
            fontSize: '14px',
            fontWeight: '700',
            alignItems: 'center',
          }}
        >
          Top Line Inputs
          <div
            style={{
              fontSize: '11px',
              color: '#919191',
              fontWeight: '100',
              // marginLeft: '10px'
            }}
          >
            The default state of the topline will be represented by "-". The
            topline input will overwrite all current values of PPG
          </div>
          <IconButton
            aria-label='close'
            onClick={() => {
              // adding telemetry log for close icon click
              trackEvent('TopLineInputs_CloseClicked', {
                scenarioId: String(scenarioId || 'new'),
                moduleId: String(moduleNameToModuleIdMap.pricing || ''),
                sourceId: String(sourceId || '')
              });
              onClose(true);
            }}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: theme => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {!isFieldsLoaded ? (
            <div style={{ margin: '8px 0px' }}>{FilterLoadingSkeleton(2)}</div>
          ) : (
            <Grid
              container
              spacing={1}
              mb={1}
              alignItems='end'
              sx={
                fetchingTopLineTableData
                  ? { opacity: 0.5, pointerEvents: 'none' }
                  : { opacity: 1 }
              }
            >
              <Grid item xs={3}>
                <Typography
                  fontSize='12px'
                  component='label'
                  display='flex'
                  justifyContent='space-between'
                >
                  Field 1
                </Typography>
                <MultiSelect
                  options={fieldsDropdownData.field1}
                  value={currActiveField.field1}
                  hasSelectAll={false}
                  onChange={fieldValue =>
                    onFieldChange('field1', fieldValue.slice(-1))
                  }
                  labelledBy='label'
                  className='multi-select-div limited-height'
                  ItemRenderer={customSingleRender}
                  closeOnChangedValue
                />
              </Grid>
              <Grid item xs={3}>
                <Typography
                  fontSize='12px'
                  component='label'
                  display='flex'
                  justifyContent='space-between'
                >
                  Field 2
                </Typography>
                <MultiSelect
                  options={fieldsDropdownData.field2}
                  value={currActiveField.field2}
                  hasSelectAll={false}
                  onChange={fieldValue =>
                    onFieldChange('field2', fieldValue.slice(-1))
                  }
                  labelledBy='label'
                  className='multi-select-div limited-height'
                  ItemRenderer={customSingleRender}
                  closeOnChangedValue
                />
              </Grid>
              <Grid item xs={3}>
                <Button
                  sx={{ ml: 1 }}
                  onClick={() => handleSubmitAction('submitDialog')}
                  variant='contained'
                >
                  Submit
                </Button>
              </Grid>
            </Grid>
          )}
          <Grid container>
            <Grid item xs={12}>
              {!fetchingTopLineTableData ? (
                <div>
                  <TopLineInputsTable
                    currActiveFieldVal={appliedFields}
                    onPeriodChange={(col, val) => {
                      latestPeriods.current[col] = val;   // cache last change
                    }}
                  />
                </div>
              ) : (
                <TableSkeletonContainer rowLength={10} headerLength={6} />
              )}
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Grid container justifyContent='flex-end'>
            <Button
              sx={{ ml: 1 }}
              onClick={() => handleApplyAction('applyDialog')}
              disabled={!enableTopLineApplyButton}
              variant='contained'
            >
              Apply
            </Button>
            <Button
              sx={{ ml: 1 }}
              onClick={() => handleResetAction('resetDialog')}
              variant='contained'
            >
              Reset
            </Button>
          </Grid>
        </DialogActions>
      </Dialog>
      <AlertDialog
        closeHandler={handleConfirmationDialogClose}
        isDialogOpen={isConfirmationDialogOpen}
        message={dialogMessage}
        title={dialogTitle}
      />
    </>
  );
}